import { useEffect, useMemo, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { Plus, X, CheckCircle, Trash, ArrowLeft, ArrowRight, Receipt, Info, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import HintButton from "@/components/HintButton";

const MONTH_NAMES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

// Componente pequeno para input inline com salvamento no blur (independente do render externo)
function InlineMoneyInput({ initial, onCommit, testid, className = "", placeholder = "0,00" }) {
  const [value, setValue] = useState(String(initial ?? ""));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    // Atualiza quando o backend mudou (mês diferente, refresh, etc.)
    setValue(String(initial ?? ""));
    setDirty(false);
  }, [initial]);

  return (
    <input
      type="number"
      step="0.01"
      min="0"
      className={`input font-mono text-right ${dirty ? "border-2 border-yellow-500" : ""} ${className}`}
      value={value}
      placeholder={placeholder}
      onChange={(e) => { setValue(e.target.value); setDirty(true); }}
      onBlur={() => {
        if (!dirty) return;
        const num = parseFloat(value);
        onCommit(isNaN(num) ? 0 : num);
        setDirty(false);
      }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      data-testid={testid}
    />
  );
}

export default function Payroll() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [showAdvance, setShowAdvance] = useState(null);
  const [showAllAdvances, setShowAllAdvances] = useState(false);

  const load = async () => {
    const [{ data: e }, { data: s }] = await Promise.all([
      api.get("/payroll/entries", { params: { year, month } }),
      api.get("/payroll/salaries", { params: { year, month } }),
    ]);
    setRows(e.rows || []);
    setSalaries(s);
  };

  useEffect(() => { load(); }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedRows = useMemo(() => {
    const grouped = {};
    for (const r of rows) {
      if (!grouped[r.collaborator_id]) grouped[r.collaborator_id] = { name: r.collaborator_name, code: r.collaborator_code, days: {} };
      grouped[r.collaborator_id].days[r.day] = r;
    }
    return Object.entries(grouped);
  }, [rows]);

  const monthTotal = useMemo(() => rows.reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);
  const advancesTotal = useMemo(() => rows.reduce((s, r) => s + Number(r.advances_total || 0), 0), [rows]);
  const netTotal = monthTotal - advancesTotal;

  // Salva apenas UM campo do salário fixo, com effective_from = mês visualizado.
  // Se já existir uma versão para este mês → atualiza. Senão cria uma nova versão.
  // Meses anteriores mantêm o valor antigo; meses subsequentes herdam este valor.
  const patchSalaryField = async (collab_id, field, newValue) => {
    const s = salaries.find((x) => x.collaborator_id === collab_id) || { base_salary: 0, day5_amount: 0, day20_amount: 0, notes: "" };
    const payload = {
      collaborator_id: collab_id,
      base_salary: Number(s.base_salary) || 0,
      day5_amount: Number(s.day5_amount) || 0,
      day20_amount: Number(s.day20_amount) || 0,
      notes: s.notes || "",
      effective_year: year,
      effective_month: month,
    };
    payload[field] = Number(newValue) || 0;
    try {
      await api.post("/payroll/salaries", payload);
      toast.success(`Salvo — vale a partir de ${MONTH_NAMES[month - 1]}/${year}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const changeMonth = (dir) => {
    let m = month + dir;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500 mb-2">FINANCEIRO</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Folha de Pagamento</h1>
          <HintButton text="Digite direto na tabela os valores de cada colaborador. Cada campo é salvo automaticamente ao clicar fora ou pressionar Enter." />
        </div>
        <button className="btn btn-ghost" onClick={() => setShowAllAdvances(true)} data-testid="all-advances-btn">
          <Receipt size={16} weight="bold" /> Todos os vales
        </button>
      </div>

      <div className="border border-yellow-500 bg-yellow-50 p-3 mb-4 text-xs flex items-start gap-2">
        <Info size={16} weight="bold" />
        <div>
          <strong>Como funciona:</strong> os valores de <strong>Dia 05</strong> e <strong>Dia 20</strong> são configurados individualmente por colaborador. Cada alteração vale <strong>a partir do mês visualizado</strong> — meses anteriores mantêm o valor antigo, meses seguintes herdam o novo. Ex.: cadastrei em Jan/2026 e reajustei em Mar/2026 → Jan e Fev ficam com o valor antigo, Mar em diante com o novo. Vales descontam apenas do mês/dia escolhido.
        </div>
      </div>

      {/* Seletor de mês */}
      <div className="flex items-center justify-between mb-4">
        <button className="btn btn-ghost" onClick={() => changeMonth(-1)} data-testid="pay-prev-month"><ArrowLeft size={16} /> Anterior</button>
        <div className="text-center">
          <div className="overline text-zinc-500">MÊS DE REFERÊNCIA</div>
          <div className="font-head text-2xl font-black" data-testid="pay-month-label">{MONTH_NAMES[month - 1]} {year}</div>
          <div className="text-xs text-zinc-500 mt-1">
            Bruto: <span className="font-mono font-bold">{fmtBRL(monthTotal)}</span> ·
            Vales: <span className="font-mono font-bold text-[#E60000]">- {fmtBRL(advancesTotal)}</span> ·
            Líquido: <span className="font-mono font-bold text-[#00A300]">{fmtBRL(netTotal)}</span>
          </div>
        </div>
        <button className="btn btn-ghost" onClick={() => changeMonth(1)} data-testid="pay-next-month">Próximo <ArrowRight size={16} /></button>
      </div>

      <div className="border border-black bg-white overflow-x-auto" data-testid="payroll-table">
        <table className="table">
          <thead>
            <tr>
              <th style={{minWidth: 180}}>Colaborador</th>
              <th className="text-right">Dia 05 (R$)</th>
              <th className="text-right">Vales dia 05</th>
              <th className="text-right">Dia 20 (R$)</th>
              <th className="text-right">Vales dia 20</th>
              <th className="text-right">Salário Mensal</th>
              <th className="text-right">Vale</th>
            </tr>
          </thead>
          <tbody>
            {groupedRows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-zinc-400">Cadastre colaboradores em /collaborators.</td></tr>}
            {groupedRows.map(([id, g]) => {
              const s = salaries.find((x) => x.collaborator_id === id) || {};
              const d5 = g.days[5] || { amount: 0, advances_total: 0 };
              const d20 = g.days[20] || { amount: 0, advances_total: 0 };
              const computedTotal = Number(s.day5_amount || 0) + Number(s.day20_amount || 0);
              return (
                <tr key={id} data-testid={`payroll-row-${id}`}>
                  <td>
                    <div className="font-semibold">{g.name}</div>
                    {g.code && <div className="font-mono text-[10px] text-zinc-500">código {g.code}</div>}
                  </td>
                  <td className="text-right">
                    <InlineMoneyInput
                      initial={s.day5_amount || 0}
                      onCommit={(v) => patchSalaryField(id, "day5_amount", v)}
                      testid={`day5-input-${id}`}
                      className="w-32"
                    />
                  </td>
                  <td className="text-right font-mono">
                    {d5.advances_total > 0 ? <span className="text-[#E60000] font-bold">- {fmtBRL(d5.advances_total)}</span> : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="text-right">
                    <InlineMoneyInput
                      initial={s.day20_amount || 0}
                      onCommit={(v) => patchSalaryField(id, "day20_amount", v)}
                      testid={`day20-input-${id}`}
                      className="w-32"
                    />
                  </td>
                  <td className="text-right font-mono">
                    {d20.advances_total > 0 ? <span className="text-[#E60000] font-bold">- {fmtBRL(d20.advances_total)}</span> : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="text-right font-mono font-bold" data-testid={`salary-total-${id}`}>
                    {fmtBRL(computedTotal)}
                    <div className="text-[9px] text-zinc-400 font-normal">
                      dia 5 + dia 20
                    </div>
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button className="text-xs underline text-[#E60000]" onClick={() => setShowAdvance({ collaborator_id: id, name: g.name })} data-testid={`add-advance-${id}`}>
                      <Plus size={11} className="inline" /> Vale
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal Advance */}
      {showAdvance && (
        <AdvanceModal
          collaborator_id={showAdvance.collaborator_id}
          collaboratorName={showAdvance.name}
          defaultYear={year}
          defaultMonth={month}
          onClose={() => setShowAdvance(null)}
          onSaved={() => { setShowAdvance(null); load(); }}
        />
      )}

      {/* Modal All Advances */}
      {showAllAdvances && (
        <AllAdvancesModal onClose={() => { setShowAllAdvances(false); load(); }} />
      )}
    </div>
  );
}

function AdvanceModal({ collaborator_id, collaboratorName, defaultYear, defaultMonth, onClose, onSaved }) {
  const [form, setForm] = useState({
    amount: "",
    date: new Date().toISOString().slice(0, 10),
    discount_year: defaultYear,
    discount_month: defaultMonth,
    discount_day: 20,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/payroll/advances", {
        collaborator_id,
        amount: parseFloat(form.amount) || 0,
        date: form.date,
        discount_year: parseInt(form.discount_year),
        discount_month: parseInt(form.discount_month),
        discount_day: parseInt(form.discount_day),
        notes: form.notes,
      });
      toast.success("Vale cadastrado — será descontado automaticamente");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="glass max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="advance-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-zinc-500">NOVO ADIANTAMENTO / VALE</div>
            <h2 className="font-head text-2xl font-black">{collaboratorName}</h2>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Valor do Vale (R$) *</label>
              <input type="number" step="0.01" min="0" required className="input font-mono" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="advance-amount" />
            </div>
            <div>
              <label className="label">Data do Vale</label>
              <input type="date" className="input font-mono" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="advance-date" />
            </div>
          </div>
          <div className="border-t pt-4">
            <div className="overline text-zinc-500 mb-2">DESCONTAR EM QUAL PARCELA</div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">Ano</label>
                <input type="number" min="2025" className="input font-mono" value={form.discount_year} onChange={(e) => setForm({ ...form, discount_year: e.target.value })} data-testid="advance-year" />
              </div>
              <div>
                <label className="label">Mês</label>
                <select className="input" value={form.discount_month} onChange={(e) => setForm({ ...form, discount_month: e.target.value })} data-testid="advance-month">
                  {MONTH_NAMES.map((n, i) => <option key={i + 1} value={i + 1}>{n}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Dia</label>
                <select className="input" value={form.discount_day} onChange={(e) => setForm({ ...form, discount_day: e.target.value })} data-testid="advance-day">
                  <option value={5}>Dia 05</option>
                  <option value={20}>Dia 20</option>
                </select>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">O vale será descontado automaticamente do valor a receber no mês/dia escolhido.</p>
          </div>
          <div>
            <label className="label">Observação</label>
            <textarea className="input min-h-[50px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" className="btn btn-ghost flex-1" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn flex-1" disabled={saving} data-testid="advance-save"><CheckCircle size={16} weight="bold" /> {saving ? "Salvando..." : "Cadastrar Vale"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AllAdvancesModal({ onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/payroll/advances").then(({ data }) => setRows(data));
  }, []);
  const del = async (id) => {
    if (!confirm("Remover vale?")) return;
    await api.delete(`/payroll/advances/${id}`);
    api.get("/payroll/advances").then(({ data }) => setRows(data));
    toast.success("Vale removido");
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="glass max-w-4xl w-full p-8 my-6" onClick={(e) => e.stopPropagation()} data-testid="all-advances-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-zinc-500">HISTÓRICO DE VALES</div>
            <h2 className="font-head text-2xl font-black">Todos os adiantamentos</h2>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="border border-black overflow-x-auto">
          <table className="table">
            <thead><tr><th>Colaborador</th><th>Data Vale</th><th>Valor</th><th>Desconto Em</th><th>Obs</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-400">Nenhum vale registrado.</td></tr>}
              {rows.map((a) => (
                <tr key={a.id} data-testid={`advance-row-${a.id}`}>
                  <td className="font-semibold">{a.collaborator_name}</td>
                  <td className="font-mono text-xs">{new Date(a.date + "T12:00:00").toLocaleDateString("pt-BR")}</td>
                  <td className="font-mono font-bold text-[#E60000]">{fmtBRL(a.amount)}</td>
                  <td className="font-mono text-xs">
                    Dia <strong>{a.discount_day}</strong> · {MONTH_NAMES[a.discount_month - 1]}/{a.discount_year}
                  </td>
                  <td className="text-xs">{a.notes || "—"}</td>
                  <td className="text-right"><button className="text-xs underline text-red-600" onClick={() => del(a.id)}><Trash size={12} className="inline mr-1" />Remover</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
